<template>
    <div v-if="!video">
        <android-file-picker
            :id="'content-video-' + fieldId"
            mode="single"
            type="Video"
            v-if="androidFilePickerOpen"
        ></android-file-picker>
        <label class="video-upload-label" @click="onLabelClick">
            <slot></slot>
            <div class="video-upload-actions">
                <input
                    ref="fileInput"
                    class="video-file-input-field"
                    @change="selectFile"
                    type="file"
                    accept="video/mp4,video/x-m4v,video/*"
                />
                <div class="button dark left">
                    Upload video file
                    <plus-icon class="icon" />
                </div>
            </div>
        </label>
    </div>
    <div class="video" v-else-if="!showPreview">
        <div type="button" class="button">Video {{ displayName }} uploaded</div>
        <button
            v-if="editMode"
            class="button red delete-file"
            @click.prevent="deleteVideo"
            type="button"
        >
            <img src="@/assets/minus.svg" class="icon" />
        </button>
    </div>
    <div class="video" v-else>
        <video controls>
            <source :src="video" type="video/mp4" />
        </video>
        <button
            v-if="editMode"
            class="button red delete-file"
            @click.prevent="deleteVideo"
            type="button"
        >
            <img src="@/assets/minus.svg" class="icon" />
        </button>
    </div>
</template>

<script>
import AndroidFilePicker from "@/components/Shared/Android-file-picker";
import { imageURL } from "@/mixins/imageURL.js";
import { isAndroid } from "mobile-device-detect";
import objectToFormData from "object-to-formdata";
import * as Sentry from "@sentry/vue";
import { Form } from "vform";
import { PlusIcon } from "vue-feather-icons";
import { isIOS, osVersion } from "mobile-device-detect";
import {
    previewUrlAfterSuccessfulUpload,
    resolveUploadFile,
    revokeBlobPreviewUrl,
} from "@/components/Shared/Book-video-lesson/video-upload-helpers";

export default {
    name: "video-upload",
    components: { AndroidFilePicker, PlusIcon },
    mixins: [imageURL],
    props: ["value", "editMode", "displayName"],
    data() {
        return {
            form: new Form({
                file: null,
            }),
            oWebViewInterface: window.nsWebViewInterface,
            showPreview: !isIOS || parseFloat(osVersion) <= 15,
            isUploadingVal: false,
            video: false,
            selectedFile: null,
            androidFilePickerOpen: false,
            fieldId: 100000 + Math.floor(Math.random() * 9999999),
        };
    },
    computed: {
        inputVal: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit("input", val);
            },
        },
        isUploading: {
            get() {
                return this.isUploadingVal;
            },
            set(val) {
                this.isUploadingVal = val;
                this.$emit("isUploadingChanged", val);
            },
        },
    },
    created() {
        this.oWebViewInterface.on("fileRecieved-content-video-" + this.fieldId, (videoData) => {
            this.androidFilePickerOpen = false;
            this.selectedFile = null;
            this.video = "data:video/mp4;base64," + String(videoData);
            this.uploadSelectedFile();
        });
    },
    beforeDestroy() {
        revokeBlobPreviewUrl(this.video);
    },
    methods: {
        onLabelClick(event) {
            if (isAndroid) {
                event.preventDefault();
                this.openFileAndroid();
            }
        },
        openFileAndroid() {
            if (isAndroid) {
                this.androidFilePickerOpen = true;
            }
        },
        selectFile(e) {
            const file = e.target.files[0];

            if (!file) {
                return;
            }

            this.selectedFile = file;
            this.video = URL.createObjectURL(file);
            this.uploadSelectedFile();
        },
        async uploadSelectedFile() {
            this.isUploading = true;
            let data = {};
            data.file = resolveUploadFile({
                selectedFile: this.selectedFile,
                videoPreviewUrl: this.video,
                dataURLtoFile: this.dataURLtoFile.bind(this),
            });
            try {
                let response = await this.form.submit("POST", "/file/upload", {
                    transformRequest: [
                        () => {
                            return objectToFormData(data, {
                                indices: true,
                                booleansAsIntegers: true,
                            });
                        },
                    ],
                });
                this.inputVal = response.data.path;
                this.selectedFile = null;
                this.video = previewUrlAfterSuccessfulUpload({
                    videoPreviewUrl: this.video,
                    showPreview: this.showPreview,
                });
                this.isUploading = false;
            } catch (e) {
                Sentry.captureException(e);
                this.$notify({ clean: true });
                this.$notify({
                    type: "error",
                    duration: 10000,
                    title: "Upload Error",
                    text: "There was an error uploading your video. Please check your connection and try again.",
                });
                this.clearSelectedVideo();
                this.isUploading = false;
            }
        },
        clearSelectedVideo() {
            revokeBlobPreviewUrl(this.video);

            this.video = null;
            this.selectedFile = null;
            this.inputVal = null;

            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
        },
        deleteVideo() {
            this.clearSelectedVideo();
            this.$emit("delete");
        },
        dataURLtoFile(dataurl, filename) {
            var arr = dataurl.split(","),
                mime = arr[0].match(/:(.*?);/)[1],
                bstr = atob(arr[1]),
                n = bstr.length,
                u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            return new File([u8arr], filename, { type: mime });
        },
    },
};
</script>

<style lang="scss" scoped>
.video-upload-label {
    display: block;
    cursor: pointer;
}
.video-upload-actions {
    position: relative;
}
.video-file-input-field {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}
video {
    width: 100%;
    border-radius: $radius;
    vertical-align: top;
}
.video {
    min-height: auto !important;
}
.formGroup {
    position: relative;
}
.delete-file {
    position: absolute;
    right: 10px;
    width: 40px !important;
    height: 40px !important;
    border-radius: 8px !important;
    top: -10px;
    box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.1);
    .icon {
        width: 9px;
    }
}
.button.blue {
    background-color: #00a0ff;
}
.video {
    background: $green_2;
    width: 100%;
    min-height: 200px;
    margin-bottom: 20px;
    border-radius: $radius;
    position: relative;
    input[type="file"] {
        opacity: 0;
    }
    &.selected {
        box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.2);
    }
}
</style>
